import React, { useState, useEffect, useContext, useCallback } from 'react';
import { UserContext } from '../contexts/UserContext';
import Pikaday from 'pikaday';
import 'pikaday/css/pikaday.css';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import '../styles/styles.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import API, { listarPDFsEva, eliminarPDF } from '../services/api';
import AnexoViewerModal from './AnexoViewerModal';
import { confirmar } from './ConfirmModal';
import { toast } from 'react-toastify';

const Perfil = () => {
  const { currentUser } = useContext(UserContext)
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorLoading, setErrorLoading] = useState(null);

  const [searchStartDate, setSearchStartDate] = useState(''); // Fecha de inicio
  const [searchEndDate, setSearchEndDate] = useState(''); // Fecha de término

  const [sortField, setSortField] = useState('fecha');
  const [sortOrder, setSortOrder] = useState('desc');
  const [anexoVer, setAnexoVer] = useState(null); // { idEva, anexo }

  const navigate = useNavigate();
  const filteredPdfs = pdfs.filter((pdf) => {
    if (!pdf.fecha) return false;

    const pdfDate = moment(pdf.fecha, 'YYYY-MM-DD'); // fecha del backend
    const start = searchStartDate ? moment(searchStartDate, 'YYYY-MM-DD') : null;
    const end = searchEndDate ? moment(searchEndDate, 'YYYY-MM-DD') : null;

    if (start && pdfDate.isBefore(start)) return false;
    if (end && pdfDate.isAfter(end)) return false;

    return true;
  });
  const fetchPDFs = useCallback(async (page = 1, perPage = 20) => {
    setLoading(true);
    setErrorLoading(null);
    try {
      const res = await listarPDFsEva(page, perPage, sortField, sortOrder);
      setPdfs(res.data);
    } catch (err) {
      console.error('Error al obtener los PDFs:', err);
      setErrorLoading('Error al obtener los PDFs');
      setPdfs([]);
    } finally {
      setLoading(false);
    }
  }, [sortField, sortOrder]);

  useEffect(() => {
    fetchPDFs();
  }, [searchStartDate, searchEndDate, sortField, sortOrder, fetchPDFs]);

  useEffect(() => {
    const startPicker = new Pikaday({
      field: document.getElementById('fecha_inicio_input'),
      format: 'DD-MM-YYYY',
      onSelect: (date) => setSearchStartDate(date.toISOString().split('T')[0]),
    });

    const endPicker = new Pikaday({
      field: document.getElementById('fecha_fin_input'),
      format: 'DD-MM-YYYY',
      onSelect: (date) => setSearchEndDate(date.toISOString().split('T')[0]),
    });

    return () => {
      startPicker.destroy();
      endPicker.destroy();
    };
  }, []);

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  const handleViewPDF = (archivoUrl) => {
    const pdfUrl = `${API.defaults.baseURL}${archivoUrl}`;
    window.open(pdfUrl, '_blank');
  };

  const handleVerAnexo = (idEva, anexo) => {
    setAnexoVer({ idEva, anexo });
  };

  const handleCerrarModalAnexo = () => {
    setAnexoVer(null);
  };
  const handleDeletePDF = async (idEva, idPdf) => {
    if (!(await confirmar('¿Está seguro que desea borrar el PDF?', { variante: 'peligro' }))) return;
    try {
      await eliminarPDF(idEva, idPdf);
      toast.success('PDF eliminado correctamente');
      fetchPDFs();
    } catch (error) {
      console.error('Error al eliminar el PDF:', error);
      toast.error('Error al eliminar el PDF');
    }
  };

  return (
    <div>
      <div className="container-wrapper">
        <h1 className="my-4">
          Hola, {currentUser
            ? (currentUser.nombre_usuario ?? currentUser.nombre ?? currentUser.email ?? 'Usuario')
            : 'Usuario'}
        </h1>
        <p>Bienvenido a tu perfil.</p>
        <button
          type="button"
          className="btn-create"
          onClick={() => navigate('/perfil/detalles')}
        >
          Ver Detalles Perfil
        </button>
        <div className='container'>
          <form className="form-search">
            <div className='form-bloack'>
              <h2>Buscar por Fecha</h2>
              <div className='form-row'>
                <div className="form-group">
                  <label>Fecha de Inicio:</label>
                  <input
                    type="text"
                    id="fecha_inicio_input"
                    name="fecha_inicio"
                    className="form-control"
                    placeholder="DD-MM-YYYY"
                    value={searchStartDate ? moment(searchStartDate).format('DD-MM-YYYY') : ''}
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label>Fecha de Término:</label>
                  <input
                    type="text"
                    id="fecha_fin_input"
                    name="fecha_fin"
                    className="form-control"
                    placeholder="DD-MM-YYYY"
                    value={searchEndDate ? moment(searchEndDate).format('DD-MM-YYYY') : ''}
                    readOnly
                  />
                </div>
              </div>
            </div>
            <button type="button" className="btn-create" onClick={() => fetchPDFs()}>
              Filtrar
            </button>
          </form>
        </div>
        {loading ? (
          <p>Cargando PDFs...</p>
        ) : errorLoading ? (
          <p className="required-text">{errorLoading}</p>
        ) : (
          <div className="container">
            <h2>Lista de Fichas Médicas (PDFs)</h2>
            <table className="table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('fecha')}>
                    Fecha {sortField === 'fecha' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th onClick={() => handleSort('fecha_vencimiento')}>
                    Vencimiento {sortField === 'fecha_vencimiento' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th onClick={() => handleSort('paciente_nombre')}>
                    Paciente {sortField === 'paciente_nombre' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th onClick={() => handleSort('paciente_rut')}>
                    RUT {sortField === 'paciente_rut' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th onClick={() => handleSort('empresa_nombre')}>
                    Empresa {sortField === 'empresa_nombre' ? (sortOrder === 'asc' ? '▲' : '▼') : ''}
                  </th>
                  <th>Anexos Adjuntos</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(filteredPdfs) && filteredPdfs.length > 0) ? (
                  filteredPdfs.map((pdf) => (
                    <tr key={pdf.id_pdf}>
                      <td>{pdf.fecha ? moment(pdf.fecha).format('DD-MM-YYYY') : '-'}</td>
                      <td>{pdf.fecha_vencimiento ? moment(pdf.fecha_vencimiento).format('DD-MM-YYYY') : '-'}</td>
                      <td>{pdf.paciente_nombre ?? '-'}</td>
                      <td>{pdf.paciente_rut ?? '-'}</td>
                      <td>{pdf.empresa_nombre ?? 'Sin empresa'}</td>
                      <td>
                        {Array.isArray(pdf.anexos) && pdf.anexos.length > 0 ? (
                          <div className="anexos-lista">
                            {pdf.anexos.map((anexo) => (
                              <button
                                key={anexo.id_anexo}
                                type="button"
                                onClick={() => handleVerAnexo(pdf.id_eva, anexo)}
                                className="btn-icon btn-secondary anexo-nombre-btn"
                                title={anexo.observaciones || anexo.original_name}
                              >
                                <i className="fas fa-paperclip"></i>{' '}
                                <span className="filename-truncado">{anexo.original_name || anexo.filename}</span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span style={{ color: '#888', fontSize: '0.85rem' }}>Sin anexos</span>
                        )}
                      </td>
                      <td>
                        <button
                          onClick={() => handleViewPDF(pdf.archivo_url)}
                          className="btn-icon"
                        >
                          <i className="fas fa-eye"></i> Ver
                        </button>
                        <button
                          onClick={() => handleDeletePDF(pdf.id_eva, pdf.id_pdf)}
                          className="btn-icon"
                        >
                          <i className="fas fa-trash"></i> Borrar
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7">No hay PDFs para mostrar.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {anexoVer && (
        <AnexoViewerModal
          idEva={anexoVer.idEva}
          anexo={anexoVer.anexo}
          onClose={handleCerrarModalAnexo}
        />
      )}
    </div>
  );
};

export default Perfil;